import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Phone, Delete } from 'lucide-react'
import { cn } from '@/lib/utils'
import { normalizeUsPhone } from '@/utils/phone'

interface DialPadProps {
  onCall: (number: string) => void
  disabled?: boolean
  /**
   * AC-17: When `mode='dtmf'`, each keypress invokes onDigit(digit) to send
   * a DTMF tone via the active Twilio Call object (call.sendDigits). The
   * number-display input and the bottom Call button are hidden.
   */
  mode?: 'dial' | 'dtmf'
  onDigit?: (digit: string) => void
}

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
]

function sanitize(raw: string): string {
  const hasPlus = raw.trimStart().startsWith('+')
  const digits = raw.replace(/\D/g, '')
  return hasPlus ? `+${digits}` : digits
}

// US-style as-you-type display: 415 → "415", 4155 → "415-5",
// 4155551 → "(415) 555-1", 14155551234 → "+1 (415) 555-1234".
function prettyUs(raw: string): string {
  if (!raw) return ''
  const plus = raw.startsWith('+')
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 0) return plus ? '+' : ''
  // 11 digits leading with 1, or already had a +
  const e164ish = plus || (digits.length === 11 && digits.startsWith('1'))
  const local = e164ish ? digits.replace(/^1/, '') : digits
  const prefix = e164ish ? '+1 ' : ''
  if (local.length <= 3) return `${prefix}${local}`
  if (local.length <= 6) return `${prefix}(${local.slice(0, 3)}) ${local.slice(3)}`
  return `${prefix}(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6, 10)}`
}

const SUB_LABELS: Record<string, string> = {
  '1': '',
  '2': 'ABC',
  '3': 'DEF',
  '4': 'GHI',
  '5': 'JKL',
  '6': 'MNO',
  '7': 'PQRS',
  '8': 'TUV',
  '9': 'WXYZ',
  '*': '',
  '0': '+',
  '#': '',
}

// ─── Haptic flash keyframe (CSS-only, no vibration API) ─────────────────────
const DIALPAD_STYLES = `
@keyframes dialPadPress {
  0%   { transform: scale(0.92); }
  60%  { transform: scale(1.02); }
  100% { transform: scale(1);    }
}
.dialpad-key-pressed {
  animation: dialPadPress 0.18s cubic-bezier(0.2,0.8,0.2,1);
}
@media (prefers-reduced-motion: reduce) {
  .dialpad-key-pressed {
    animation: none;
  }
}
`

export function DialPad({ onCall, disabled, mode = 'dial', onDigit }: DialPadProps) {
  const [number, setNumber] = useState('')
  const [lastDigit, setLastDigit] = useState<string | null>(null)
  const [pressedKey, setPressedKey] = useState<string | null>(null)

  const isDtmf = mode === 'dtmf'

  const handleKeyPress = useCallback((key: string) => {
    // Animate the key
    setPressedKey(key)
    window.setTimeout(() => setPressedKey(null), 200)

    if (isDtmf) {
      // AC-17: send DTMF tone on the remote leg via call.sendDigits(digit).
      onDigit?.(key)
      setLastDigit(key)
      window.setTimeout(() => setLastDigit((prev) => (prev === key ? null : prev)), 200)
      return
    }
    setNumber(prev => prev + key)
  }, [isDtmf, onDigit])

  const handleBackspace = useCallback(() => {
    setNumber(prev => prev.slice(0, -1))
  }, [])

  const trimmed = number.trim()
  const normalized = useMemo(() => normalizeUsPhone(trimmed), [trimmed])
  const isValid = normalized !== null

  const handleCall = useCallback(() => {
    if (normalized) {
      onCall(normalized)
    }
  }, [normalized, onCall])

  // Physical keyboard: map keypresses to dialpad buttons
  useEffect(() => {
    const VALID = new Set(['0','1','2','3','4','5','6','7','8','9','*','#'])
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (VALID.has(e.key)) {
        e.preventDefault()
        handleKeyPress(e.key)
      } else if (!isDtmf && e.key === 'Backspace') {
        e.preventDefault()
        handleBackspace()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isDtmf, handleKeyPress, handleBackspace])

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: DIALPAD_STYLES }} />
      <div
        className={cn('flex flex-col', !isDtmf && 'animate-fade-up')}
        style={{
          gap: isDtmf ? 8 : 14,
          padding: isDtmf ? '8px 12px' : '14px 16px 16px',
          flex: 1,
          minHeight: 0,
          justifyContent: isDtmf ? 'flex-start' : 'space-between',
        }}
      >

        {/* Hero number display — dial mode only */}
        {!isDtmf && (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px 6px',
              minHeight: 60,
            }}
          >
            <input
              type="tel"
              value={prettyUs(number)}
              onChange={e => setNumber(sanitize(e.target.value))}
              placeholder="Enter number"
              inputMode="tel"
              aria-label="Phone number"
              className="placeholder:text-[rgba(255,255,255,0.22)]"
              style={{
                flex: 1,
                // A flex item's default `min-width: auto` refuses to shrink
                // below its intrinsic content width, and an <input size=20> at
                // 44px display type wants ~660px. In a 320px panel that pushed
                // the field 200px past the panel edge and clipped the number
                // the operator had just dialled — visible even when empty,
                // because the placeholder overflowed too.
                minWidth: 0,
                background: 'transparent',
                textAlign: 'center',
                // Hero typography, tabular for stable cadence.
                fontFamily: 'var(--font-display)',
                // 16px floor satisfied because clamped min is 32px (iOS won't auto-zoom).
                fontSize: 'clamp(32px, 7vw, 44px)',
                fontWeight: 600,
                letterSpacing: '-0.035em',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
                color: '#FFFFFF',
                border: 'none',
                outline: 'none',
                paddingRight: number ? 48 : 0,
                transition: 'padding-right 0.15s ease',
              }}
            />
            {number && (
              <button
                type="button"
                onClick={handleBackspace}
                aria-label="Delete last digit"
                style={{
                  position: 'absolute',
                  right: 12,
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--r-pill)',
                  border: '0.5px solid var(--midnight-border)',
                  background: 'var(--midnight-surface-2, #14141A)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
                  touchAction: 'manipulation',
                }}
              >
                <Delete style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
        )}

        {/* DTMF header — show last-pressed digit */}
        {isDtmf && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px 0',
              gap: 6,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.40)',
              }}
            >
              {lastDigit ? `Sent: ${lastDigit}` : 'Tap to send DTMF'}
            </span>
          </div>
        )}

        {/* Thin divider in dial mode */}
        {!isDtmf && (
          <div
            style={{
              height: '0.5px',
              background: 'rgba(255,255,255,0.08)',
              marginBottom: 4,
            }}
          />
        )}

        {/* 3×4 key grid — 72×72 circular buttons */}
        <div className="grid grid-cols-3 gap-4">
          {KEYS.flat().map(key => {
            const isPressed = pressedKey === key
            const isDtmfActive = isDtmf && lastDigit === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleKeyPress(key)}
                disabled={isDtmf && disabled}
                aria-label={`Dial ${key}`}
                className={isPressed ? 'dialpad-key-pressed' : undefined}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 'var(--r-pill)',
                  border: '0.5px solid var(--midnight-border)',
                  background: isDtmfActive
                    ? 'rgba(168,230,206,0.18)'
                    : 'var(--midnight-surface-2, #14141A)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: (isDtmf && disabled) ? 'not-allowed' : 'pointer',
                  touchAction: 'manipulation',
                  transition: 'background 0.15s ease',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  opacity: isDtmf && disabled ? 0.5 : isDtmf ? 0.75 : 1,
                  // DTMF mode: buttons are slightly dimmer + smaller text
                  margin: '0 auto',
                }}
                onPointerDown={(e) => {
                  if (!(isDtmf && disabled)) {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'rgba(255,255,255,0.10)'
                  }
                }}
                onPointerUp={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = isDtmfActive
                    ? 'rgba(168,230,206,0.18)'
                    : 'var(--midnight-surface-2, #14141A)'
                }}
                onPointerLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = isDtmfActive
                    ? 'rgba(168,230,206,0.18)'
                    : 'var(--midnight-surface-2, #14141A)'
                }}
              >
                {/* Digit — top */}
                <span
                  style={{
                    fontSize: isDtmf ? 20 : 24,
                    fontWeight: 400,
                    letterSpacing: '-0.01em',
                    color: isDtmfActive ? 'var(--midnight-mint)' : '#FFFFFF',
                    lineHeight: 1,
                  }}
                >
                  {key}
                </span>
                {/* Letters — bottom, iOS style */}
                {SUB_LABELS[key] && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.10em',
                      color: 'rgba(255,255,255,0.40)',
                      lineHeight: 1,
                      marginTop: 3,
                    }}
                  >
                    {SUB_LABELS[key]}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Call button — dial mode only */}
        {!isDtmf && (
          <div style={{ paddingTop: 8 }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleCall}
              disabled={!isValid || disabled}
              aria-label="Place call"
              title={!trimmed ? 'Enter a number' : !isValid ? 'Enter a valid US number' : undefined}
              style={{ width: '100%', height: 56 }}
            >
              <Phone style={{ width: 18, height: 18 }} />
              Call
            </button>
          </div>
        )}
      </div>
    </>
  )
}
